import { useState, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { BrandedLoader } from "@/components/ui/BrandedLoader";
import DevBypassIndicator from "@/components/admin/AdminDebugIndicator";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthModalProvider, useAuthModal } from "@/contexts/AuthModalContext";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient, attachAuthCacheReset } from "@/lib/queryClient";
import ScrollToTop from "./components/ScrollToTop";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { useDelayedLoading } from "@/hooks/useDelayedLoading";
import Noindex from "./components/pivot/Noindex";

// Lazy-load auth-related components to keep Supabase out of the initial bundle
const Login = lazy(() => import("./pages/Login"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ProtectedRoute = lazy(() => import("./components/ProtectedRoute"));
const NotFound = lazy(() => import("./pages/NotFound"));
const CookieConsent = lazy(() => import("./components/CookieConsent"));
const CookieSettingsSheet = lazy(() => import("./components/CookieSettingsSheet"));

const HomePivot = lazy(() => import("./pages/HomePivot"));
const BusinessDetail = lazy(() => import("./pages/BusinessDetail"));

const LazyAuthModal = lazy(() => import("./components/auth/AuthModal"));

// Lazy-loaded routes (not needed on initial page load)
const Index = lazy(() => import("./pages/Index"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Cookies = lazy(() => import("./pages/Cookies"));
const ReviewsPage = lazy(() => import("./pages/Reviews"));
const DashboardLayout = lazy(() => import("./components/dashboard/DashboardLayout"));
const DashboardOverview = lazy(() => import("./pages/dashboard/DashboardOverview"));
const StepBrainstorm = lazy(() => import("./pages/dashboard/stap/StepBrainstorm"));
const BrainstormSubstep = lazy(() => import("./pages/dashboard/stap/BrainstormSubstep"));
const StepLegaal = lazy(() => import("./pages/dashboard/stap/StepLegaal"));
const LegaalSubstep = lazy(() => import("./pages/dashboard/stap/LegaalSubstep"));
const StepVerloning = lazy(() => import("./pages/dashboard/stap/StepVerloning"));
const StepFinancieel = lazy(() => import("./pages/dashboard/stap/StepFinancieel"));
const FinancieelSubstep = lazy(() => import("./pages/dashboard/stap/FinancieelSubstep"));
const StepVoorbereiding = lazy(() => import("./pages/dashboard/stap/StepVoorbereiding"));
const StepStart = lazy(() => import("./pages/dashboard/stap/StepStart"));
const Downloads = lazy(() => import("./pages/dashboard/Downloads"));
const Documenten = lazy(() => import("./pages/dashboard/Documenten"));
const JeBedrijven = lazy(() => import("./pages/dashboard/JeBedrijven"));
const TipsTricks = lazy(() => import("./pages/dashboard/TipsTricks"));
const Community = lazy(() => import("./pages/dashboard/Community"));
const MijnTraject = lazy(() => import("./pages/dashboard/MijnTraject"));
const BerekenJeSituatie = lazy(() => import("./pages/dashboard/BerekenJeSituatie"));
const Tools = lazy(() => import("./pages/dashboard/Tools"));
const Partners = lazy(() => import("./pages/dashboard/Partners"));
const DashboardProfile = lazy(() => import("./pages/dashboard/DashboardProfile"));
const DashboardNotificaties = lazy(() => import("./pages/dashboard/DashboardNotificaties"));
const DashboardBeveiliging = lazy(() => import("./pages/dashboard/DashboardBeveiliging"));
const DashboardAbonnement = lazy(() => import("./pages/dashboard/DashboardAbonnement"));
const DashboardHelpcentrum = lazy(() => import("./pages/dashboard/DashboardHelpcentrum"));
const DashboardFeedback = lazy(() => import("./pages/dashboard/DashboardFeedback"));
const DashboardContact = lazy(() => import("./pages/dashboard/DashboardContact"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminGebeurtenissen = lazy(() => import("./pages/admin/AdminGebeurtenissen"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminDigest = lazy(() => import("./pages/admin/AdminDigest"));
const AdminGamification = lazy(() => import("./pages/admin/AdminGamification"));
const AdminNieuwsScanner = lazy(() => import("./pages/admin/AdminNieuwsScanner"));
const AdminStartergidsLeads = lazy(() => import("./pages/admin/AdminStartergidsLeads"));
const AdminLegalAcceptances = lazy(() => import("./pages/admin/AdminLegalAcceptances"));
const AdminReviewWidgets = lazy(() => import("./pages/admin/AdminReviewWidgets"));

const AuthModalRenderer = () => {
  const { isOpen } = useAuthModal();
  const location = useLocation();

  if (!isOpen && location.pathname !== "/login") {
    return null;
  }

  return (
    <Suspense fallback={location.pathname === "/login" ? <AppLoadingFallback /> : null}>
      <LazyAuthModal />
    </Suspense>
  );
};

const AppLoadingFallback = () => {
  // Geen globale "Ola pola"-loader meer bij het lazy-laden van routes.
  // De pagina verschijnt zodra de chunk geladen is; specifieke pagina's
  // (zoals Login) tonen hun eigen loader waar dat zinvol is.
  return null;
};

const App = () => {
  const [cookieSettingsOpen, setCookieSettingsOpen] = useState(false);
  // Eén keer registreren: bij SIGNED_OUT wordt de hele query-cache geleegd.
  attachAuthCacheReset();

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthModalProvider>
          <ScrollToTop />
          <AuthModalRenderer />
          <ErrorBoundary>
            <Suspense fallback={<AppLoadingFallback />}>
              <Routes>
              <Route path="/" element={<HomePivot />} />
              <Route path="/businesses/:slug" element={<BusinessDetail />} />
              <Route path="/old" element={<><Noindex /><Index /></>} />
              <Route path="/onboarding" element={<><Noindex /><Onboarding /></>} />
              <Route path="/login" element={<><Noindex /><Login /></>} />
              <Route path="/reset-password" element={<><Noindex /><ResetPassword /></>} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/voorwaarden" element={<Terms />} />
              <Route path="/cookies" element={<Cookies />} />
              <Route path="/reviews" element={<><Noindex /><ReviewsPage /></>} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Noindex />
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<DashboardOverview />} />
                <Route path="stap/brainstorm" element={<StepBrainstorm />} />
                <Route path="stap/brainstorm/:substepId" element={<BrainstormSubstep />} />
                <Route path="stap/legaal" element={<StepLegaal />} />
                <Route path="stap/legaal/:substepId" element={<Navigate to="/dashboard/stap/legaal" replace />} />
                <Route path="stap/financien" element={<StepFinancieel />} />
                <Route path="stap/financien/:substepId" element={<Navigate to="/dashboard/stap/financien" replace />} />
                <Route path="stap/branding" element={<StepVoorbereiding />} />
                <Route path="stap/branding/:substepId" element={<Navigate to="/dashboard/stap/branding" replace />} />
                <Route path="stap/marketing" element={<StepVerloning />} />
                <Route path="stap/marketing/:substepId" element={<Navigate to="/dashboard/stap/marketing" replace />} />
                <Route path="stap/launch" element={<StepStart />} />
                <Route path="stap/launch/:substepId" element={<Navigate to="/dashboard/stap/launch" replace />} />
                <Route path="downloads" element={<Downloads />} />
                <Route path="documenten" element={<Documenten />} />
                <Route path="je-bedrijven" element={<JeBedrijven />} />
                <Route path="tips-tricks" element={<TipsTricks />} />
                <Route path="community" element={<Community />} />
                <Route path="mijn-traject" element={<MijnTraject />} />
                <Route path="bereken-je-situatie" element={<BerekenJeSituatie />} />
                <Route path="tools" element={<Tools />} />
                <Route path="partners" element={<Partners />} />
                <Route path="profiel" element={<DashboardProfile />} />
                <Route path="notificaties" element={<DashboardNotificaties />} />
                <Route path="beveiliging" element={<DashboardBeveiliging />} />
                <Route path="abonnement" element={<DashboardAbonnement />} />
                <Route path="helpcentrum" element={<DashboardHelpcentrum />} />
                <Route path="feedback" element={<DashboardFeedback />} />
                <Route path="contact" element={<DashboardContact />} />
              </Route>
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <Noindex />
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/admin/analytics" replace />} />
                <Route path="gebruikers" element={<AdminUsers />} />
                <Route path="gebeurtenissen" element={<AdminGebeurtenissen />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="gamification" element={<AdminGamification />} />
                <Route path="digest" element={<AdminDigest />} />
                <Route path="nieuws-scanner" element={<AdminNieuwsScanner />} />
                <Route path="startergids-leads" element={<AdminStartergidsLeads />} />
                <Route path="legale-disclaimer" element={<AdminLegalAcceptances />} />
                <Route path="review-widgets" element={<AdminReviewWidgets />} />
              </Route>
              <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
          <Suspense fallback={null}>
            <CookieConsent onOpenSettings={() => setCookieSettingsOpen(true)} />
            <CookieSettingsSheet open={cookieSettingsOpen} onClose={() => setCookieSettingsOpen(false)} />
          </Suspense>
          <DevBypassIndicator />
          </AuthModalProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
